<script setup lang="ts">
import { IconCircleCheck } from '@tabler/icons-vue'
import { computed } from 'vue'

import BaseIcon from '@tix/ds/components/atoms/BaseIcon.vue'
import ExternalLinkButton from '@tix/ds/components/atoms/ExternalLinkButton.vue'
import RouterLinkButton from '@tix/ds/components/atoms/RouterLinkButton.vue'

import BaseCard from '@/components/BaseCard.vue'
import ImageBento from '@/components/ImageBento.vue'
import Price from '@/components/elements/PriceDisplay.vue'

import type { GalleryImage } from '@/types/images'

export type DonationFrequency = 'one-time' | 'monthly' | 'annually'

const DONATION_FREQUENCY_LABELS: Record<DonationFrequency, string> = {
  'one-time': 'One-time',
  monthly: 'Monthly',
  annually: 'Annually',
}

export type DonationCompleteViewProps = {
  id: string
  receiptEmail: string
  orderNumber: string
  donationAmount: number
  frequency?: DonationFrequency
  images?: GalleryImage[]
}

const props = defineProps<DonationCompleteViewProps>()

const frequencyLabel = computed(() => (props.frequency ? DONATION_FREQUENCY_LABELS[props.frequency] : undefined))
</script>

<template>
  <div class="donation-complete-view">
    <ImageBento v-if="images?.length" class="gallery" :images="images" />

    <div class="content">
      <section class="success-message">
        <span class="success-icon">
          <BaseIcon aria-hidden="true" class="success-check-icon" :icon="IconCircleCheck" />
        </span>

        <h2 class="success-title">Thank you!</h2>

        <p class="success-subtitle">We greatly appreciate your support</p>

        <div class="confirmation-message">
          <p>
            We emailed your donation receipt to
            <span class="receipt-email">{{ receiptEmail }}</span>
          </p>

          <p>Thank you for making a difference.</p>
        </div>
      </section>

      <BaseCard class="summary-card">
        <h3 class="summary-title">Your donation summary</h3>

        <dl class="summary-rows">
          <div class="summary-row">
            <dt>Order number</dt>
            <dd>{{ orderNumber }}</dd>
          </div>

          <div class="summary-row">
            <dt>Donation amount</dt>
            <dd><Price :value="donationAmount" /></dd>
          </div>

          <div v-if="frequencyLabel" class="summary-row">
            <dt>Frequency</dt>
            <dd>{{ frequencyLabel }}</dd>
          </div>
        </dl>
      </BaseCard>

      <div class="actions">
        <ExternalLinkButton hierarchy="primary" href="/events" intent="confirm">Browse our events</ExternalLinkButton>

        <RouterLinkButton hierarchy="secondary" intent="neutral" :to="{ name: 'donation', params: { id: props.id } }"
          >Make another donation
        </RouterLinkButton>
      </div>
    </div>
  </div>
</template>

<style scoped>
.donation-complete-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-2) var(--space-3);

  .gallery {
    flex-shrink: 0;
    width: 100%;
    max-width: 1376px;
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-2);
    width: 100%;
    max-width: 768px;
  }

  .success-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-1-5);
    width: 100%;
    text-align: center;
    color: var(--text-primary);
    word-break: break-word;

    .success-icon {
      display: inline-flex;
      flex-shrink: 0;
      color: var(--success-icon-color);

      .success-check-icon {
        font-size: 28.667px; /* BaseIcon 1.5em → 43px */
      }
    }

    .success-title {
      font-size: var(--font-size-h2);
      font-weight: var(--font-weight-bold);
      line-height: var(--line-height-heading);
      margin: 0;
    }

    .success-subtitle {
      font-size: var(--font-size-h3);
      font-weight: var(--font-weight-bold);
      line-height: var(--line-height-heading);
      margin: 0;
    }

    .confirmation-message {
      font-size: var(--font-size-body);
      line-height: var(--line-height-body);

      p {
        margin: 0;
      }

      .receipt-email {
        font-size: var(--font-size-h5);
        font-weight: var(--font-weight-bold);
      }
    }
  }

  .summary-card {
    .summary-title {
      font-size: var(--font-size-h3);
      font-weight: var(--font-weight-bold);
      line-height: var(--line-height-heading);
      margin: 0 0 var(--space-1-5);
      text-align: center;
      color: var(--text-primary);
    }

    .summary-rows {
      display: flex;
      flex-direction: column;
      gap: var(--space-1);
      margin: 0;
    }

    .summary-row {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--space-1);
      font-size: var(--font-size-body);
      font-weight: var(--font-weight-semibold);
      line-height: var(--line-height-body);
      color: var(--text-primary);

      &:not(:last-child) {
        border-bottom: var(--divider);
        padding-bottom: var(--space-1);
      }

      dt,
      dd {
        margin: 0;
      }

      dd {
        text-align: right;
      }
    }
  }

  .actions {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-1);
    width: 100%;

    &:deep(.base-button) {
      width: 100%;
    }

    @media (--desktop-experience) {
      flex-flow: row wrap;
      justify-content: center;
      align-items: center;

      &:deep(.base-button) {
        width: auto;
      }
    }
  }
}
</style>
